import {
	GET_ISSUING_AUTHORITIES_REQUEST,
	GET_ISSUING_AUTHORITIES_SUCCESS,
	GET_ISSUING_AUTHORITIES_ERROR,
	GET_ISSUING_AUTHORITY_REQUEST,
	GET_ISSUING_AUTHORITY_SUCCESS,
	GET_ISSUING_AUTHORITY_ERROR,
	CREATE_ISSUING_AUTHORITY_REQUEST,
	CREATE_ISSUING_AUTHORITY_SUCCESS,
	CREATE_ISSUING_AUTHORITY_ERROR,
	EDIT_ISSUING_AUTHORITY_REQUEST,
	EDIT_ISSUING_AUTHORITY_SUCCESS,
	EDIT_ISSUING_AUTHORITY_ERROR,
	DELETE_ISSUING_AUTHORITY_REQUEST,
	DELETE_ISSUING_AUTHORITY_SUCCESS,
	DELETE_ISSUING_AUTHORITY_ERROR
} from "@/actions"

import {
	issuingAuthorityService
} from "@/services"

import { isEmpty, addNewFilter, decodeLaravelValidationErrors } from "@/utilities"

const state = {
	issuingAuthorities: [],
	issuingAuthority: {},
	loading: false,
	loadedOnce: false
}

const getters = {
	issuingAuthorities: state => state.issuingAuthorities,

	issuingAuthoritiesSelectOptions: state => {
		return state.issuingAuthorities.map(ia => {
			let abbr = isEmpty(ia.abbreviation) ? "" : `(${ia.abbreviation})`
			let label = (`${ia.name} ${abbr}`).trim()
			return { value: ia.uuid, label: label }
		}).sort((a, b) => a.label.localeCompare(b.label))
	},

	isFilter: state => uuid => state.issuingAuthorities.find(ia => ia.uuid == uuid)
}

const actions = {
	async getIssuingAuthorities({ commit }) {
		commit("GET_ISSUING_AUTHORITIES_REQUEST")
		const response = await issuingAuthorityService.getIssuingAuthorities();
		const { status, data } = response
		switch (status) {
			case 200:
				commit("GET_ISSUING_AUTHORITIES_SUCCESS", data)
				break
			default:
				commit("GET_ISSUING_AUTHORITIES_ERROR")
		}
		return response
	},
	async getIssuingAuthority({ commit }, params) {
		commit("GET_ISSUING_AUTHORITY_REQUEST")
		const response = await issuingAuthorityService.getIssuingAuthority(params)
		const { status, data } = response
		switch (status) {
			case 200:
				commit("GET_ISSUING_AUTHORITY_SUCCESS", data)
				break
			default:
				commit("GET_ISSUING_AUTHORITY_ERROR")
				break
		}
		return response
	},
	async createIssuingAuthority({ commit, dispatch }, params) {
		commit("CREATE_ISSUING_AUTHORITY_REQUEST")
		const response = await issuingAuthorityService.createIssuingAuthority(params)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					commit("CREATE_ISSUING_AUTHORITY_SUCCESS", data);
					dispatch("alert/createAlertSuccess", "Issuing Authority created successfully", { root: true })
				}

				if (data?.error || data?.errors) {
					commit("CREATE_ISSUING_AUTHORITY_ERROR");
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data?.errors) : "Opps. Something went wrong.", { root: true })
				}
				break
			default:
				commit("CREATE_ISSUING_AUTHORITY_ERROR")
		}
		
		return response
	},
	async editIssuingAuthority({ commit, dispatch }, params) {
		commit("EDIT_ISSUING_AUTHORITY_REQUEST")
		const uuid = params.uuid
		const response = await issuingAuthorityService.editIssuingAuthority(params, uuid)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					commit("EDIT_ISSUING_AUTHORITY_SUCCESS", data);
					dispatch("alert/createAlertSuccess", "Issuing Authority updated successfully", { root: true })
				}

				if (data?.error || data?.errors) {
					commit("CREATE_ISSUING_AUTHORITY_ERROR");
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data?.errors) : "Opps. Something went wrong.", { root: true })
				}
				break
			default:
				commit("EDIT_ISSUING_AUTHORITY_ERROR")
		}
		return response
	},
	async deleteIssuingAuthority({ commit, dispatch }, uuid) {
		commit("DELETE_ISSUING_AUTHORITY_REQUEST")
		const response = await issuingAuthorityService.deleteIssuingAuthority(uuid)
		const { status, data } = response
		switch (status) {
			case 200:
				if (data?.success) {
					const { issuing_authority } = data;
					commit("DELETE_ISSUING_AUTHORITY_SUCCESS", { ...issuing_authority, uuid });
					dispatch("alert/createAlertSuccess", "Issuing Authority deleted successfully", { root: true })
				}

				if(data?.error) {
					commit("DELETE_ISSUING_AUTHORITY_ERROR")
				}

				if (data?.error || data?.errors) {
					commit("CREATE_ISSUING_AUTHORITY_ERROR");
					dispatch("alert/createAlertErrors", data?.errors ? decodeLaravelValidationErrors(data?.errors) : "Opps. Something went wrong.", { root: true })
				}
				break
			default:
				commit("DELETE_ISSUING_AUTHORITY_ERROR")
		}
		return response
	}
}

const mutations = {
	[GET_ISSUING_AUTHORITIES_REQUEST]: state => {
		state.loading = true
	},
	[GET_ISSUING_AUTHORITIES_SUCCESS]: (state, data) => {
		state.issuingAuthorities = addNewFilter(state.issuingAuthorities, data?.issuing_authorities ?? [])
		state.loading = false
	},
	[GET_ISSUING_AUTHORITIES_ERROR]: state => {
		state.loading = false
		state.error = true
	},
	[GET_ISSUING_AUTHORITY_REQUEST]: state => {
		state.loading = true
	},
	[GET_ISSUING_AUTHORITY_SUCCESS]: (state, data) => {
		const { issuing_authority } = data;
		state.issuingAuthorities = addNewFilter(state.issuingAuthorities, [issuing_authority])
		state.loading = false
	},
	[GET_ISSUING_AUTHORITY_ERROR]: state => {
		state.loading = false
	},

	[CREATE_ISSUING_AUTHORITY_REQUEST]: state => {
		state.loading = true
	},
	[CREATE_ISSUING_AUTHORITY_SUCCESS]: (state, data) => {
		const { issuing_authority } = data;
		state.issuingAuthorities = addNewFilter(state.issuingAuthorities, [issuing_authority])
		state.loading = false
	},
	[CREATE_ISSUING_AUTHORITY_ERROR]: state => {
		state.loading = false
	},

	[EDIT_ISSUING_AUTHORITY_REQUEST]: state => {
		state.loading = true
	},
	[EDIT_ISSUING_AUTHORITY_SUCCESS]: (state, data) => {
		const { issuing_authority } = data;
		state.issuingAuthorities = addNewFilter(state.issuingAuthorities, [issuing_authority])
		state.loading = false
	},
	[EDIT_ISSUING_AUTHORITY_ERROR]: state => {
		state.loading = false
	},

	[DELETE_ISSUING_AUTHORITY_REQUEST]: state => {
		state.loading = true
	},
	[DELETE_ISSUING_AUTHORITY_SUCCESS]: (state, issuing_authority) => {
		const { uuid, deleted_at } = issuing_authority;

		if(!isEmpty(deleted_at)) {
			state.issuingAuthorities = addNewFilter(state.issuingAuthorities, [issuing_authority])
		} else {
			state.issuingAuthorities = state.issuingAuthorities.filter(ia => ia.uuid != uuid)
		}

		state.loading = false
	},
	[DELETE_ISSUING_AUTHORITY_ERROR]: state => {
		state.loading = false
	}

}

export const issuingAuthority = {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}